import { useSelectedStudentIdContext } from "#/components/exam-results/context/studentsList/context";
import GeneralInfoTable from "#/components/exam-results/GeneralInfoTable";
import Infographic from "#/components/exam-results/infographic";
import Loading from "#/components/loading";
import {
  getExamSummaryInfo,
  getMainResultSet
} from "#/services/rest/gwt/exam-results/views/type-1/multi-user.api";
import { type MainResultSet } from "#/services/rest/gwt/exam-results/views/type-1/multi-user.types";
import { useParams, useSearchParams } from "@monta/router-react";
import { forwardRef, useEffect, useMemo, useState } from "react";
import GeneralInfoHeader from "./GeneralInfoHeader";
import MultiLesson from "./MultiLesson";
import styles from "./styles.module.scss";

const GeneralInfo = forwardRef<HTMLDivElement, object>((_, contentRef) => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const examTakingId = Number(params["examTakingId"]);
  const { selectedStudentId } = useSelectedStudentIdContext();
  const isSimulator = searchParams.get("isSimulator");
  const [isSingleLesson, setIsSingleLesson] = useState(false);
  const [bookData, setBookData] = useState<MainResultSet[]>([]);
  const [topicData, setTopicData] = useState<MainResultSet[]>([]);
  const [overallInfo, setOverallInfo] = useState<MainResultSet>();

  const { data, isLoading, mutate } = getMainResultSet.useMutation({
    onSuccess: (res) => {
      const overallInfo = res.find((item) => item.id === null);
      setOverallInfo(overallInfo);
      const actualData = res.filter((item) => item.orderInExam !== null);
      setBookData(actualData.filter((item) => item.isModule));
      setTopicData(
        actualData.filter((item) => !item.isModule && item.id !== null)
      );
    }
  });

  const { data: examInfo, isLoading: examInfoLoading } =
    getExamSummaryInfo.useQuery({
      request: { body: { examTakingDetailId: examTakingId } }
    });

  useEffect(() => {
    if (bookData.length === 1 && topicData.length === 1)
      setIsSingleLesson(true);
  }, [bookData, topicData]);

  useEffect(() => {
    if (selectedStudentId)
      mutate({
        body: {
          examTakingDetailId: examTakingId,
          studentId: selectedStudentId
        }
      });
  }, [examTakingId, selectedStudentId]);

  const totalQuestionCount = useMemo(() => {
    if (isSingleLesson && overallInfo)
      return (
        overallInfo.correctAnswers +
        overallInfo.noAnswers +
        overallInfo.incorrectAnswers
      );
  }, [overallInfo, isSingleLesson]);

  const studentInfo = localStorage.getItem("currentStudentInfo");

  return (
    <>
      {isLoading || examInfoLoading ? (
        <Loading />
      ) : (
        <div ref={contentRef} className={styles["print"]}>
          <div className={styles["print__header"]}>
            کارنامه دانش آموز، {studentInfo}
          </div>
          {examInfo && overallInfo && (
            <GeneralInfoHeader
              takingDate={examInfo.startTakingDateTime}
              name={examInfo.examName}
              rank={overallInfo.rank}
              level={overallInfo.level}
              totalStudentNO={examInfo.participantNo}
              presentStudentNo={examInfo.presentParticipantNo}
              tierNO={overallInfo.tierNO}
              growthStatus={overallInfo.growthStatus}
              gradeId={examInfo.grade}
              majorId={examInfo.majorId}
            />
          )}

          {isSimulator || isSingleLesson ? (
            <div className={styles["subtitle"]}>بررسی درس به درس</div>
          ) : (
            <></>
          )}
          {data && isSimulator ? (
            <GeneralInfoTable
              data={data}
              totalStudentNO={examInfo?.participantNo ?? 0}
            />
          ) : overallInfo && examInfo && isSingleLesson ? (
            <Infographic
              correctCount={overallInfo.correctAnswers}
              examName={examInfo.examName}
              finalMark={overallInfo.mark}
              wrongCount={overallInfo.incorrectAnswers}
              uncheckedCount={overallInfo.noAnswers}
              totalCount={totalQuestionCount ?? 0}
              date={overallInfo.takingDate ?? ""}
            />
          ) : (
            examInfo && (
              <MultiLesson
                overallInfo={examInfo}
                bookData={bookData}
                topicData={topicData}
              />
            )
          )}
        </div>
      )}
    </>
  );
});

GeneralInfo.displayName = "GeneralInfo";

export default GeneralInfo;
